"use client";

import { useEffect, useState } from "react";
import { useRobotTxtStore } from "@/stores/robotTxtStore";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionSubject } from "@/utils/permissions";
import PermissionGuard from "@/components/PermissionGuard";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  RefreshCw,
  Save,
  RotateCcw,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

export default function RobotTxtPage() {
  const {
    robotTxt,
    isLoading,
    isUpdating,
    isResetting,
    isToggling,
    error,
    fetchRobotTxt,
    updateRobotTxt,
    resetRobotTxt,
    toggleStatus,
    clearError,
    clearValidation,
  } = useRobotTxtStore();

  const { canRead, canUpdate } = usePermissions();
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");

  // Kiểm tra quyền truy cập trang
  const canAccessPage = canRead(PermissionSubject.ROBOT_TXT);

  useEffect(() => {
    if (canAccessPage) {
      fetchRobotTxt();
    }
  }, [canAccessPage, fetchRobotTxt]);

  useEffect(() => {
    if (robotTxt) {
      setContent(robotTxt.content || "");
      setDescription(robotTxt.description || "");
    }
  }, [robotTxt]);

  const handleSave = async () => {
    if (!canUpdate(PermissionSubject.ROBOT_TXT)) {
      return;
    }

    const success = await updateRobotTxt({
      content,
      description,
    });

    if (success) {
      await fetchRobotTxt();
    }
  };

  const handleReset = async () => {
    if (!canUpdate(PermissionSubject.ROBOT_TXT)) {
      return;
    }

    const success = await resetRobotTxt();
    if (success) {
      await fetchRobotTxt();
    }
  };

  const handleToggleStatus = async () => {
    if (!canUpdate(PermissionSubject.ROBOT_TXT)) {
      return;
    }

    const success = await toggleStatus();
    if (success) {
      await fetchRobotTxt();
    }
  };

  // Bỏ kiểm tra theo yêu cầu

  const handleRefresh = () => {
    fetchRobotTxt();
    clearError();
    clearValidation();
  };

  if (!canAccessPage) {
    return (
      <DashboardLayout>
        <div className="p-10 text-center">
          <div className="text-red-600 text-lg font-medium mb-2">
            Không có quyền truy cập
          </div>
          <div className="text-gray-500">
            Bạn không có quyền truy cập trang quản lý robots.txt
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header Section */}
        <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
              Quản lý Robots.txt
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <p className="text-gray-500">
                Cấu hình file robots.txt cho website
              </p>
              {robotTxt && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                    robotTxt.isActive
                      ? "bg-green-50 text-green-700 ring-green-200"
                      : "bg-red-50 text-red-700 ring-red-200"
                  }`}
                >
                  {robotTxt.isActive ? "Đang hoạt động" : "Đã tắt"}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex items-center gap-2 hover:cursor-pointer"
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              Làm mới
            </Button>

            <PermissionGuard
              subject={PermissionSubject.ROBOT_TXT}
              action="update"
            >
              <Button
                onClick={handleToggleStatus}
                variant="outline"
                className="flex items-center gap-2 hover:cursor-pointer"
                disabled={isToggling || !robotTxt}
              >
                {robotTxt?.isActive ? (
                  <ToggleLeft className="h-4 w-4" />
                ) : (
                  <ToggleRight className="h-4 w-4" />
                )}
                {isToggling
                  ? "Đang xử lý..."
                  : robotTxt?.isActive
                  ? "Tắt"
                  : "Bật"}
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Nội dung file robots.txt
                </h2>
              </div>

              <div className="p-6">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Đang tải...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="text-red-600 text-lg font-medium mb-2">
                      Lỗi khi tải dữ liệu
                    </div>
                    <div className="text-gray-500 mb-4">{error}</div>
                    <Button
                      onClick={handleRefresh}
                      className="hover:cursor-pointer"
                    >
                      Thử lại
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nội dung file robots.txt *
                      </label>
                      <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                        rows={12}
                        placeholder="User-agent: *&#10;Disallow: /"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mô tả (tùy chọn)
                      </label>
                      <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Mô tả về file robots.txt"
                        maxLength={500}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {description.length}/500 ký tự
                      </p>
                    </div>

                    {/* Bỏ phần kết quả kiểm tra theo yêu cầu */}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 pt-4">
                      <PermissionGuard
                        subject={PermissionSubject.ROBOT_TXT}
                        action="update"
                      >
                        <Button
                          onClick={handleSave}
                          disabled={isUpdating}
                          className="bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 hover:cursor-pointer"
                        >
                          <Save className="h-4 w-4" />
                          {isUpdating ? "Đang lưu..." : "Cập nhật"}
                        </Button>
                      </PermissionGuard>

                      {/* Bỏ nút Kiểm tra theo yêu cầu */}

                      <PermissionGuard
                        subject={PermissionSubject.ROBOT_TXT}
                        action="update"
                      >
                        <Button
                          onClick={handleReset}
                          disabled={isResetting}
                          variant="outline"
                          className="flex items-center gap-2 hover:cursor-pointer"
                        >
                          <RotateCcw className="h-4 w-4" />
                          {isResetting ? "Đang reset..." : "Reset mặc định"}
                        </Button>
                      </PermissionGuard>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Thông tin robots.txt
              </h3>
              {robotTxt ? (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">Trạng thái:</span>
                    <span
                      className={`ml-2 inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        robotTxt.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {robotTxt.isActive ? "Hoạt động" : "Tắt"}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">
                      Cập nhật lần cuối:
                    </span>
                    <span className="ml-2 text-sm text-gray-900">
                      {new Date(robotTxt.updatedAt).toLocaleString("vi-VN")}
                    </span>
                  </div>
                  {robotTxt.lastModifiedBy && (
                    <div>
                      <span className="text-sm text-gray-500">
                        Người cập nhật:
                      </span>
                      <span className="ml-2 text-sm text-gray-900">
                        {typeof robotTxt.lastModifiedBy === "string"
                          ? robotTxt.lastModifiedBy
                          : robotTxt.lastModifiedBy.name ||
                            robotTxt.lastModifiedBy.username ||
                            robotTxt.lastModifiedBy._id}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Chưa có thông tin</p>
              )}
            </div>

            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                Hướng dẫn sử dụng
              </h3>
              <div className="text-sm text-blue-800 space-y-2">
                <p>
                  • <strong>User-agent:</strong> Chỉ định bot nào được áp dụng
                </p>
                <p>
                  • <strong>Disallow:</strong> Cấm bot truy cập đường dẫn
                </p>
                <p>
                  • <strong>Allow:</strong> Cho phép bot truy cập đường dẫn
                </p>
                <p>
                  • <strong>Sitemap:</strong> Đường dẫn đến sitemap
                </p>
                <p>
                  • <strong>Crawl-delay:</strong> Thời gian chờ giữa các request
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
